import { useEffect, useMemo } from 'react';
import { brandBlogRecommendations } from '../../../../shared/brand-blog/recommendations';

type BrandBlogPageProps = {
  publisher: string;
  brandName: string;
  englishName: string;
  tagline: string;
};

const BRAND_BLOG_URL = '/blog';

function coverUrl(path: string) {
  return path.startsWith('/') ? `https://sonbook.net${path}` : path;
}

export default function BrandBlogPage({
  publisher,
  brandName,
  englishName,
  tagline,
}: BrandBlogPageProps) {
  const articles = useMemo(
    () =>
      brandBlogRecommendations
        .filter((article) => article.publisher === publisher)
        .sort((left, right) => right.publishDate.localeCompare(left.publishDate)),
    [publisher],
  );

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute('content') ?? null;

    document.title = `${brandName}｜閱讀推薦`;
    description?.setAttribute(
      'content',
      `${brandName}出版書籍與主題閱讀推薦，整理崧博出版主站的品牌精選內容。`,
    );

    return () => {
      document.title = previousTitle;
      if (description && previousDescription !== null) {
        description.setAttribute('content', previousDescription);
      }
    };
  }, [brandName]);

  return (
    <main className="min-h-screen bg-background-50 text-foreground-900">
      <section className="relative overflow-hidden bg-primary-950 pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-accent-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-primary-500/25 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-6 md:px-10">
          <div className="max-w-3xl">
            <div className="mb-6 flex items-center gap-3 text-xs font-label tracking-[0.22em] text-background-200/70">
              <span>{englishName}</span>
              <span className="h-px w-10 bg-accent-400/70" />
              <span>BLOG</span>
            </div>
            <h1 className="font-heading text-4xl font-semibold leading-tight text-background-50 md:text-6xl">
              {brandName}
              <span className="mt-3 block text-accent-300">閱讀推薦</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-background-100/75 md:text-lg">
              {tagline}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-background-100/70">
              <span className="rounded-full border border-background-100/20 bg-background-50/10 px-4 py-2">
                {articles.length} 本品牌精選
              </span>
              <a
                href={BRAND_BLOG_URL}
                className="inline-flex items-center gap-2 rounded-full border border-accent-300/50 px-4 py-2 text-accent-200 transition-colors hover:bg-accent-400/15 hover:text-background-50"
              >
                查看崧博出版主站
                <i className="ri-arrow-right-up-line text-xs" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-6 md:px-10">
          <div className="mb-10 flex items-end justify-between gap-6 border-b border-background-300/70 pb-5">
            <div>
              <p className="text-xs font-label tracking-[0.2em] text-primary-600">
                PUBLISHER PICKS
              </p>
              <h2 className="mt-2 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
                {brandName}的本期選書
              </h2>
            </div>
            <span className="hidden text-sm text-foreground-500 sm:block">
              依主站推薦日期排列
            </span>
          </div>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {articles.map((article, index) => (
                <a
                  key={article.id}
                  href={`${BRAND_BLOG_URL}/${article.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-background-300/70 bg-background-50 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary-400/60 hover:shadow-xl"
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-white p-5">
                    <img
                      src={coverUrl(article.coverImage)}
                      alt={article.title}
                      loading={index < 3 ? 'eager' : 'lazy'}
                      className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                    />
                    <span className="absolute left-5 top-5 rounded-full bg-primary-950/85 px-3 py-1 text-[11px] font-label tracking-wide text-background-50">
                      {article.category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="mb-3 flex items-center justify-between gap-3 text-xs text-foreground-400">
                      <span>{article.publishDate}</span>
                      <span className="font-label tracking-[0.16em] text-primary-600">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <h3 className="font-heading text-xl font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                      {article.title}
                    </h3>
                    <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-600">
                      {article.excerpt}
                    </p>
                    <div className="mt-6 flex items-center justify-between gap-3 border-t border-background-200/80 pt-4 text-sm">
                      <span className="line-clamp-1 text-accent-700">{article.tagline}</span>
                      <span className="flex shrink-0 items-center gap-1 font-medium text-primary-700">
                        閱讀全文
                        <i className="ri-arrow-right-line text-xs transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <i className="ri-book-open-line text-4xl text-primary-500" />
              <h3 className="mt-4 font-heading text-xl font-semibold text-foreground-900">
                本期尚未有可同步的品牌推薦
              </h3>
              <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-foreground-500">
                主站目前尚未收錄 {brandName} 的每月推薦內容；品牌最新出版資訊仍可由品牌首頁查閱。
              </p>
            </div>
          )}

          <div className="mt-12 rounded-2xl border border-primary-300/40 bg-primary-50/60 p-6 md:flex md:items-center md:justify-between md:gap-8">
            <div>
              <p className="font-heading text-lg font-semibold text-primary-900">
                想看完整推薦與編輯導讀？
              </p>
              <p className="mt-1 text-sm leading-relaxed text-primary-800/75">
                前往崧博出版主站，瀏覽所有出版品牌的推薦內容。
              </p>
            </div>
            <a
              href={BRAND_BLOG_URL}
              className="mt-4 inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-primary-700 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800 md:mt-0"
            >
              瀏覽全部推薦
              <i className="ri-arrow-right-line text-xs" />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}


